import type {AxiosInstance, InternalAxiosRequestConfig} from 'axios'

const MAX_CONCURRENT_REQUESTS = 5

let activeRequests = 0
const queue: Array<{resolve: () => void}> = []

function processQueue() {
  if (queue.length === 0) return
  if (activeRequests >= MAX_CONCURRENT_REQUESTS) return
  activeRequests++
  const {resolve} = queue.shift()!
  resolve()
}

function releaseSlot() {
  activeRequests--
  processQueue()
}

/**
 * Attaches a concurrency-throttling interceptor to an Axios instance.
 * Limits the number of simultaneous in-flight requests to MAX_CONCURRENT_REQUESTS.
 * Excess requests are queued and released as slots become available.
 * This prevents crashes caused by a large number of simultaneous API calls
 * (e.g. expand-all triggering many sub-row loads at once).
 */
export function attachThrottleInterceptor(axiosInstance: AxiosInstance): AxiosInstance {
  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) =>
      new Promise(resolve => {
        queue.push({resolve: () => resolve(config)})
        processQueue()
      }),
    error => Promise.reject(error),
  )

  axiosInstance.interceptors.response.use(
    response => {
      releaseSlot()
      return response
    },
    error => {
      releaseSlot()
      return Promise.reject(error)
    },
  )

  return axiosInstance
}
